import { useEffect, useState, type ReactNode } from 'react'
import { Zeichen, type ZeichenName } from './Zeichen.js'

/**
 * Paket 12 · Der Rahmen der Control Plane.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum ein eigener Rahmen und nicht der Büro-Rahmen mit einem Eintrag mehr
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Bis Paket 12 war der Betreiberbereich ein Reiter IM Büro. Links standen
 * daneben Übersicht, Rechnungen, Offerten, Zahlungen, Rapporte, Projekte,
 * Kunden, Leistungen – die Arbeitsmittel eines Handwerksbetriebs.
 *
 * Der Betreiber schreibt aber keine Rechnung für einen Kundenbetrieb, erfasst
 * keinen Rapport und pflegt keinen Leistungskatalog. Diese Einträge waren für
 * ihn nicht bloss überflüssig, sie waren irreführend: Sie legten nahe, dass
 * man von hier aus in fremde Geschäftsdaten kommt. Man kommt nicht, und die
 * Oberfläche soll das zeigen und nicht nur der Server.
 *
 * Also ein eigener Rahmen mit einer eigenen Informationsarchitektur. Der
 * Betreiber hat sein Büro weiterhin – er wechselt über «Zu meinem Betrieb»
 * hinüber. Zwei Rollen, zwei Rahmen, ein Konto.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Sieben Punkte, nicht fünfzehn
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Gruppiert nach der Frage, die jemand hat, wenn er hier hereinkommt:
 *
 *   Wie steht es?        → Übersicht
 *   Wer sind sie?        → Betriebe
 *   Was verkaufen wir?   → Pakete und Nutzung
 *   Läuft die Technik?   → Automatik
 *   Wer hat was getan?   → Protokoll
 *
 * Testbetriebe und Vertragszustände sind keine eigenen Punkte, sondern Filter
 * auf der Betriebsliste. Ein Menüeintrag, der nur eine vorgefilterte Liste
 * öffnet, verdoppelt den Weg zur selben Tabelle.
 */

export type Controlbereich =
  | 'uebersicht'
  | 'betriebe'
  | 'pakete'
  | 'automatik'
  | 'protokoll'
  | 'sicherheit'

export interface Controleintrag {
  id: Controlbereich
  label: string
  icon: ZeichenName
  /** Eine Zahl am Eintrag – nur wenn sie etwas bedeutet. */
  zahl?: number
}

export interface Controlgruppe {
  titel: string
  eintraege: Controleintrag[]
}

export function controlGruppen(zahlen: {
  betriebe?: number
  aufmerksamkeit?: number
  automatikFehler?: number
}): Controlgruppe[] {
  return [
    {
      titel: 'Plattform',
      eintraege: [
        {
          id: 'uebersicht',
          label: 'Übersicht',
          icon: 'uebersicht',
          zahl: zahlen.aufmerksamkeit || undefined
        },
        { id: 'betriebe', label: 'Betriebe', icon: 'kunde', zahl: zahlen.betriebe }
      ]
    },
    {
      titel: 'Produkt',
      eintraege: [
        { id: 'pakete', label: 'Pakete & Nutzung', icon: 'leistung' },
        {
          id: 'automatik',
          label: 'Automatik',
          icon: 'kalender',
          zahl: zahlen.automatikFehler || undefined
        }
      ]
    },
    {
      titel: 'Nachweis',
      eintraege: [
        { id: 'protokoll', label: 'Protokoll', icon: 'haken' },
        { id: 'sicherheit', label: 'Sicherheit', icon: 'einstellung' }
      ]
    }
  ]
}

export function ControlShell({
  aktiv,
  aufWechsel,
  zahlen,
  benutzer,
  aufBuero,
  aufAbmelden,
  kinder
}: {
  aktiv: Controlbereich
  aufWechsel: (id: Controlbereich) => void
  zahlen: { betriebe?: number; aufmerksamkeit?: number; automatikFehler?: number }
  benutzer: string
  aufBuero: () => void
  aufAbmelden: () => void
  kinder: ReactNode
}): JSX.Element {
  /*
    Die Seitenleiste am Telefon.

    Sie steht auf breiten Bildschirmen fest und wird darunter zur Schublade.
    Nicht «alles untereinander»: Eine Navigation, die sich über den ganzen
    Bildschirm legt, ist auf einem Tablet nicht Navigation, sondern eine
    Seite, die man erst wegklicken muss.
  */
  const [offen, setOffen] = useState(false)

  /*
    Jeder Klick in der Navigation schliesst die Schublade – auch der auf den
    Bereich, in dem man schon ist.

    Nur auf den Wechsel zu hören, reichte nicht: Wer am Telefon «Betriebe»
    antippt, während «Betriebe» bereits offen ist, ändert nichts, und die
    Schublade blieb über der Liste liegen. Der nächste Klick ging dann ins
    Leere, weil die Navigation ihn abfing. Gemessen mit der
    Control-Plane-Probe, die genau dort hängen blieb.
  */
  useEffect(() => {
    setOffen(false)
  }, [aktiv])

  const waehle = (id: Controlbereich): void => {
    setOffen(false)
    aufWechsel(id)
  }

  const gruppen = controlGruppen(zahlen)

  return (
    /*
      `office` steht mit im Klassennamen, und das ist keine Nachlässigkeit.

      Die Farbmarken von Raporto – `--bg`, `--surface`, `--accent`, die ganze
      Nachtpalette – sind unter `.office` definiert, nicht auf `:root`.
      Dieselbe Klasse trägt auch `color-scheme: dark` und das Raumlicht im
      Hintergrund. Ohne sie stünde die Control Plane in den Vorgabefarben des
      Browsers da: weisser Grund, schwarze Schrift, keine Abstände – genau so
      sah das erste Bildschirmfoto aus.

      Die Control Plane erbt damit die Marke und setzt nur ihr eigenes Raster
      darüber.
    */
    <div className={`office cp-rahmen${offen ? ' cp-offen' : ''}`} id="control-plane">
      <aside className="cp-nav" id="control-nav">
        <div className="cp-marke">
          <span className="cp-marke-punkt" aria-hidden="true" />
          <div>
            <strong>Raporto</strong>
            <span className="cp-marke-unter">Control Plane</span>
          </div>
        </div>

        <nav className="cp-navliste" aria-label="Bereiche der Control Plane">
          {gruppen.map((g) => (
            <div className="cp-navgruppe" key={g.titel}>
              <p className="cp-navtitel">{g.titel}</p>
              {g.eintraege.map((e) => (
                <button
                  key={e.id}
                  id={`cp-nav-${e.id}`}
                  type="button"
                  className={e.id === aktiv ? 'active' : undefined}
                  aria-current={e.id === aktiv ? 'page' : undefined}
                  onClick={() => waehle(e.id)}
                >
                  <Zeichen name={e.icon} groesse={17} />
                  <span>{e.label}</span>
                  {e.zahl !== undefined && <span className="cp-navzahl">{e.zahl}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="cp-navfuss">
          {/* Der Weg zurück in die eigene Rolle. Er steht unten und nicht in
              der Liste: Er wechselt nicht den Bereich, sondern den Rahmen. */}
          <button type="button" id="cp-zum-buero" onClick={aufBuero}>
            <Zeichen name="rechnung" groesse={17} />
            <span>Zu meinem Betrieb</span>
          </button>
          <button type="button" id="cp-abmelden" onClick={aufAbmelden}>
            <Zeichen name="einstellung" groesse={17} />
            <span>Abmelden</span>
          </button>
          <p className="cp-benutzer">{benutzer}</p>
        </div>
      </aside>

      {/* Die Fläche, die die Schublade wieder schliesst. Nur schmal sichtbar. */}
      <button
        type="button"
        className="cp-schatten"
        aria-label="Navigation schliessen"
        onClick={() => setOffen(false)}
      />

      <div className="cp-inhalt">
        <header className="cp-kopf">
          <button
            type="button"
            className="cp-menueknopf"
            id="cp-menue"
            aria-label="Navigation öffnen"
            aria-expanded={offen}
            onClick={() => setOffen((o) => !o)}
          >
            <Zeichen name="mehr" groesse={20} />
          </button>
          <div className="cp-kopf-titel">
            <span className="cp-augenbraue">Control Plane</span>
            <h1>{TITEL[aktiv]}</h1>
          </div>
        </header>
        <main className="cp-flaeche">{kinder}</main>
      </div>
    </div>
  )
}

const TITEL: Record<Controlbereich, string> = {
  uebersicht: 'Übersicht',
  betriebe: 'Betriebe',
  pakete: 'Pakete & Nutzung',
  automatik: 'Automatik',
  protokoll: 'Protokoll',
  sicherheit: 'Sicherheit'
}
